/**
 * Two-step dialog for removing an item from a group.
 *
 * Extracted from ItemDetailPanel.tsx to reduce component complexity.
 * Step 1: Select which group to remove from (picker dialog).
 * Step 2: Confirm the removal (AlertDialog).
 */
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { useRemoveItemFromGroup } from '@/hooks/useMyItems';
import { toast } from 'sonner';
import { T, RADIUS, SHADOW } from '@/lib/tokens';

interface ItemGroup {
  id: string;
  name: string;
}

interface RemoveFromGroupDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemId: string;
  itemName: string;
  groups: ItemGroup[];
  defaultGroupId?: string;
  onRemoved: () => void;
}

/**
 *
 * @param root0
 * @param root0.open
 * @param root0.onOpenChange
 * @param root0.itemId
 * @param root0.itemName
 * @param root0.groups
 * @param root0.defaultGroupId
 * @param root0.onRemoved
 */
export function RemoveFromGroupDialog({
  open,
  onOpenChange,
  itemId,
  itemName,
  groups,
  defaultGroupId,
  onRemoved,
}: RemoveFromGroupDialogProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(defaultGroupId || '');
  const removeFromGroup = useRemoveItemFromGroup();

  const selectedGroupName = groups.find((g) => g.id === selectedGroup)?.name || 'this group';

  const handleConfirm = async () => {
    if (!selectedGroup) return;
    try {
      await removeFromGroup.mutateAsync({ itemId, groupId: selectedGroup });
      toast.success('Item removed from group');
      setConfirmOpen(false);
      onOpenChange(false);
      setSelectedGroup('');
      onRemoved();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to remove from group');
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove from Group</DialogTitle>
            <DialogDescription>Choose which group to remove "{itemName}" from.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Group</Label>
              <Select value={selectedGroup} onValueChange={setSelectedGroup}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a group" />
                </SelectTrigger>
                <SelectContent>
                  {groups.map((g) => (
                    <SelectItem key={g.id} value={g.id}>
                      {g.name || 'This group'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <button
              onClick={() => {
                if (selectedGroup) setConfirmOpen(true);
              }}
              disabled={!selectedGroup}
              style={{
                width: '100%',
                padding: '13px',
                borderRadius: RADIUS.md,
                border: 'none',
                background: !selectedGroup ? T.surfaceSubtle : T.danger,
                color: !selectedGroup ? T.textMuted : T.white,
                fontSize: 15,
                fontWeight: 600,
                cursor: !selectedGroup ? 'not-allowed' : 'pointer',
                fontFamily: 'inherit',
                boxShadow: selectedGroup ? SHADOW.btn.danger : 'none',
              }}
            >
              Continue
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove "{itemName}" from {selectedGroupName}. The item will remain in your
              personal inventory.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              disabled={removeFromGroup.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {removeFromGroup.isPending ? 'Removing…' : 'Remove from Group'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
